import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import logo from '@/img/logo.png';

export default function Navbar() {
  const { t, i18n } = useTranslation('home');
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const navItems = [
    { label: t('nav.rtkHost'), href: '#products' },
    { label: t('nav.scenarios'), href: '#scenarios' },
    { label: t('nav.antennaParts'), href: '#accessories' },
    { label: t('nav.selectionQuote'), href: '#compare' },
  ];

  const currentLang = i18n.language === 'en' ? 'EN' : '中文';

  const toggleLang = () => {
    const next = i18n.language === 'zh-CN' ? 'en' : 'zh-CN';
    i18n.changeLanguage(next);
    setLangOpen(false);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-white/95 backdrop-blur-sm border-b border-slate-100' : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-8 flex items-center justify-between h-14">
        <a href="#top" className="flex items-center gap-2 select-none" aria-label="Bitilinks Home">
          <span className="w-8 h-8 text-white flex items-center justify-center text-sm font-bold rounded-md">
           <img src={logo} alt="Bitilinks" className="w-full h-full object-contain" />
          </span>
          <span className={`text-sm font-semibold transition-colors ${scrolled ? 'text-slate-900' : 'text-slate-900'}`}>
            {t('nav.brand')}
            <span className="ml-1 text-xs font-normal text-slate-500">{t('nav.subtitle')}</span>
          </span>
        </a>

        <nav className="hidden md:flex items-center gap-6">
          {navItems.map((item) => (
            <a
              key={item.href}
              href={item.href}
              className="text-sm text-slate-600 hover:text-orange-600 transition-colors"
            >
              {item.label}
            </a>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          {/* Language switcher */}
          <div className="relative">
            <button
              onClick={() => setLangOpen(!langOpen)}
              className="flex items-center gap-1.5 text-sm text-slate-600 hover:text-orange-600 transition-colors px-2 py-1 rounded-md hover:bg-slate-50"
              aria-label={t('lang.switch')}
            >
              <i className="ri-global-line"></i>
              <span>{currentLang}</span>
              <i className={`ri-arrow-down-s-line text-xs transition-transform ${langOpen ? 'rotate-180' : ''}`}></i>
            </button>
            {langOpen && (
              <div className="absolute right-0 top-full mt-1 bg-white border border-slate-200 rounded-lg shadow-sm py-1 min-w-[100px]">
                <button
                  onClick={toggleLang}
                  className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                >
                  {i18n.language === 'zh-CN' ? t('lang.en') : t('lang.zh')}
                </button>
              </div>
            )}
          </div>

          <a
            href="#quote"
            className="inline-flex items-center text-sm font-medium bg-orange-600 text-white px-4 py-2 rounded-md hover:bg-orange-700 transition-colors whitespace-nowrap"
          >
            {t('nav.getQuote')}
          </a>
        </div>

        <button
          className="md:hidden w-9 h-9 flex items-center justify-center text-slate-700"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label={t('nav.toggleNav')}
        >
          <i className={`ri-${mobileOpen ? 'close' : 'menu'}-line text-xl`}></i>
        </button>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-white border-b border-slate-100 px-4 py-3 flex flex-col gap-3">
          {navItems.map((item) => (
            <a
              key={item.href}
              href={item.href}
              className="text-sm text-slate-600 py-2"
              onClick={() => setMobileOpen(false)}
            >
              {item.label}
            </a>
          ))}
          {/* Mobile language switch */}
          <button
            onClick={() => { toggleLang(); setMobileOpen(false); }}
            className="text-sm text-slate-600 py-2 flex items-center gap-2"
          >
            <i className="ri-global-line"></i>
            {i18n.language === 'zh-CN' ? t('lang.en') : t('lang.zh')}
          </button>
          <a
            href="#quote"
            className="text-sm font-medium bg-orange-600 text-white px-4 py-2 rounded-md text-center"
            onClick={() => setMobileOpen(false)}
          >
            {t('nav.getQuote')}
          </a>
        </div>
      )}
    </header>
  );
}